import { Button } from '@/components/button/button'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/card/card'
import { DatePicker } from '@/components/date-picker/date-picker'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/dialog/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/form/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/select/select'

import { cn } from '@/lib/utils'

import { RESIDENCE_OPTIONS } from './constants'
import { useUpdateVisaBulletinInfoDialog } from './hooks/use-update-visa-bulletin-info-dialog'

export interface UpdateVisaBulletinInfoDialogProps {
  caseType: string | undefined
}

export const UpdateVisaBulletinInfoDialog = ({ caseType }: UpdateVisaBulletinInfoDialogProps) => {
  const {
    form,
    dataUscisFormCategories,
    dataCountries,
    isDialogOpen,
    isDisabledCheckButton,
    isPendingUpdateCaseExtraInfoButton,
    handleChangeDialogOpen,
    handleSubmitForm,
  } = useUpdateVisaBulletinInfoDialog()

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleChangeDialogOpen}>
      <DialogContent>
        {/* Only for a11y */}
        <DialogHeader>
          <DialogTitle>Check Visa bulletin</DialogTitle>
          <DialogDescription hidden>Select your form category, and country of origin</DialogDescription>
        </DialogHeader>
        {/* Card content */}
        <Card className={cn('mt-[30px]')}>
          <CardHeader>
            <CardDescription>Select your form category, and country of origin</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form className={cn('space-y-4')} onSubmit={form.handleSubmit(handleSubmitForm)}>
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{caseType} Form Category*</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a Category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {dataUscisFormCategories?.map(({ alias, code, name }) => (
                            <SelectItem key={code} value={code}>
                              {alias && (
                                <span className={cn('text-sm font-medium text-slate-700')}>{`${alias} - `}</span>
                              )}
                              <span className={cn('text-sm font-normal text-slate-700')}>{name}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="countryOfOrigin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country of origin*</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {dataCountries?.map(({ code, description }) => (
                            <SelectItem key={code} value={code}>
                              {description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="priorityDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Priority date*</FormLabel>
                      <DatePicker
                        formatString="MM-DD-YYYY"
                        placeholder="MM-DD-YYYY"
                        value={field.value}
                        onSelect={field.onChange}
                      />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="residence"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your Residence*</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select Residence" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {RESIDENCE_OPTIONS.map(({ label, value }) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
                <Button variant="default" loading={isPendingUpdateCaseExtraInfoButton} disabled={isDisabledCheckButton}>
                  Check
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  )
}
